import { MappedEntity, SnakeMeta } from '@toggle/toggle';
import { useMemo } from 'react';

import { IndexedTsPoint } from '~/components/snake-chart/snake-chart-utils';
import { useAPI } from '~/hooks/use-api/useAPI';
import {
  fetchSnakeByName,
  fetchSnakeMetaV2,
} from '~/services/overview-widget/overview-widget-service';
import { priceSuffix } from '~/utils/currency/currency';

const HORIZON_NUMBER_OF_DAYS = 180;
export const SPY_ARCA_ENTITY = 'spy_arca';
export const SPY_ARCA_SNAKE = 'spy_arca.price.ds';

interface UseSpyArcaProps {
  thresholdsDates?: Record<string, string>;
}

interface UseSpyArcaReturnProps {
  tsData: SpxTsData[];
  entity?: MappedEntity;
  priceSuffix: string;
  name?: string;
  snakeMeta?: SnakeMeta;
  refetch: () => Promise<void>;
  loading: boolean;
}
export interface SpxTsData extends IndexedTsPoint {
  direction?: string;
}

export const useSpyArca = ({
  thresholdsDates,
}: UseSpyArcaProps): UseSpyArcaReturnProps => {
  const {
    data: snakeByName,
    refetch,
    loading,
  } = useAPI(
    abortSignal =>
      fetchSnakeByName(SPY_ARCA_SNAKE, HORIZON_NUMBER_OF_DAYS, abortSignal),
    {
      mapDataFn: ({ result, meta }) => ({
        tsData: result.data,
        meta,
      }),
    }
  );

  const { data: snakeMetaV2 } = useAPI(
    abortSignal => fetchSnakeMetaV2(SPY_ARCA_SNAKE, abortSignal),
    {
      mapDataFn: result => ({
        meta: result[SPY_ARCA_SNAKE],
      }),
    }
  );

  const suffix: string = useMemo(() => {
    return priceSuffix(
      snakeMetaV2?.meta.display_format,
      snakeMetaV2?.meta.currency
    );
  }, [snakeMetaV2?.meta]);

  const tsData: SpxTsData[] = useMemo(() => {
    if (!snakeByName?.tsData) {
      return [];
    }
    return snakeByName.tsData.map((p, i) => {
      return {
        index: i,
        value: p.value,
        date: new Date(p.index),
        direction: thresholdsDates?.[p.index],
      };
    });
  }, [snakeByName?.tsData, thresholdsDates]);

  return {
    tsData,
    priceSuffix: suffix,
    name: snakeByName?.meta.name,
    snakeMeta: snakeMetaV2?.meta,
    refetch,
    loading,
  };
};
